import type { User } from '@auth0/auth0-react';
import { useTracking } from '@ui/hooks/useTracking';
import { useCallback, useMemo } from 'react';

const LOGIN_TRACKED_KEY = 'auth_login_tracked';

/**
 * Internal hook for auth-specific tracking functionality.
 * @internal This hook is for useAuthWithTracking implementation only.
 */
export const useAuthTracking = () => {
  const { track, identify, alias, people } = useTracking();

  const trackUserIdentity = useCallback(
    (user: User) => {
      try {
        const userId = user.sub?.split('|')?.[1] ?? 'none';
        const orgId = user.org_id?.split('_')?.[1] ?? 'none';
        const orgName = user['https://ocula.tech/actions/organizations']?.display_name ?? 'Undefined';
        const oculaStaff = user.email?.includes('@ocula.tech');

        alias(userId);
        identify(userId);
        people.set({
          'Organisation ID': orgId,
          'Organisation name': orgName,
          'Ocula Staff': oculaStaff,
        });
      } catch (error) {
        console.error('User identity tracking failed:', error);
        throw error;
      }
    },
    [identify, alias, people],
  );

  const trackLogin = useCallback(
    (user: User) => {
      try {
        const hasTrackedLogin = localStorage.getItem(LOGIN_TRACKED_KEY) === 'true';
        if (hasTrackedLogin) {
          return;
        }

        localStorage.setItem(LOGIN_TRACKED_KEY, 'true');

        try {
          trackUserIdentity(user);

          const isNewUser = user.updated_at === user.created_at;
          const authMethod = user.sub?.split('|')[0] ?? 'unknown';

          track(isNewUser ? 'Signup completed' : 'Login completed', {
            'location journey': 'Authentication',
            'location page': isNewUser ? 'Signup' : 'Login',
            'location component': 'Auth0',
            actor: 'User',
            'auth method': authMethod,
            'is new user': isNewUser ? 1 : 0,
          });
        } catch (error) {
          // Clean up the flag if tracking fails to allow retry
          localStorage.removeItem(LOGIN_TRACKED_KEY);
          console.error('Login tracking failed:', error);
          throw error;
        }
      } catch (error) {
        console.error('Local storage access failed:', error);
        throw error;
      }
    },
    [track, trackUserIdentity],
  );

  const trackAuthInitiated = useCallback(() => {
    try {
      track('Auth initiated', {
        'location journey': 'Authentication',
        'location page': 'Login',
        'location component': 'Auth0',
        actor: 'User',
      });
    } catch (error) {
      console.error('Auth initiation tracking failed:', error);
    }
  }, [track]);

  const trackLogout = useCallback(() => {
    try {
      localStorage.removeItem(LOGIN_TRACKED_KEY);
      track('Logout initiated', {
        'location journey': 'Authentication',
        'location page': 'Logout',
        'location component': 'Auth0',
        actor: 'User',
      });
    } catch (error) {
      console.error('Logout tracking failed:', error);
    }
  }, [track]);

  const clearLoginTracking = useCallback(() => {
    try {
      localStorage.removeItem(LOGIN_TRACKED_KEY);
    } catch (error) {
      console.error('Failed to clear login tracking:', error);
    }
  }, []);

  return useMemo(
    () => ({
      trackLogin,
      trackAuthInitiated,
      trackLogout,
      clearLoginTracking,
    }),
    [trackLogin, trackAuthInitiated, trackLogout, clearLoginTracking],
  );
};
